import type { Finding } from './shared';
import type { TodayChecks } from './todayData';
import { isMenuLinkFinding, navIssues, type NavPart } from './navChecks';
import { andList } from './pageChecks';

/**
 * Design's working copy bar: every check that matters for Publish, listed
 * under the button (backlog c4b822db, "Design Publish Checks" spec). The
 * findings are the site's as they would be AFTER this publish: pages that go
 * live with it count as live, so a Home page in the same publish passes.
 *
 *   fix    red, holds Publish: the front page, or anything else blocking
 *   check  amber: worth a look, publishing still works
 *   live   grey: the pages that go live with the theme
 */

export type PublishAction =
  | 'addHome'
  | 'keepArticles'
  | 'addPages'
  | 'editMenu'
  | 'reviewPages'
  | 'keepSections';

/** A section the live site has that this publish drops (finding section_removed). */
export interface RemovedSection {
  slug: string;
  name: string;
  articles: number;
}

export interface PublishRow {
  key: string;
  kind: 'fix' | 'check' | 'live';
  text: string;
  sub: string;
  actions: Array<{ id: PublishAction; label: string }>;
}

export interface PublishChecks {
  rows: PublishRow[];
  /** "Before you publish · 1 to fix, 1 to check", or "What publishes". */
  head: string;
  /** Set while a fix row is open: why Publish waits. */
  blockedTip: string | null;
  /** Where Edit the menu goes. */
  menuPart: NavPart;
}

export interface PublishCheckInput {
  /** The site's findings, after this publish. */
  findings: Finding[];
  /** Pages and forms that go live with the site. */
  goesLive: Array<{ kind: 'page' | 'form'; title: string | null }>;
  /** The pages' and forms' own checks (a page in the publish with something to fix). */
  otherChecks?: TodayChecks | null;
  /** The working template, when the look is one: its name and the pages it makes. */
  template?: { name: string; pages: Array<{ title: string; slug: string }> } | null;
  /** Addresses of pages that exist but aren't live (and don't go with this publish). */
  draftPaths?: Set<string>;
}

const TAG: Record<PublishRow['kind'], string> = {
  fix: 'Fix first',
  check: 'Check',
  live: 'Goes live',
};
export const tagOf = (kind: PublishRow['kind']) => TAG[kind];

const slugOf = (href: string) => href.replace(/[?#].*$/, '').replace(/^\/+|\/+$/g, '');

/** "About, Tips…": a few link names, then an ellipsis. */
function someNames(names: string[], max = 3) {
  const unique = [...new Set(names)];
  return unique.length > max ? `${unique.slice(0, max).join(', ')}…` : unique.join(', ');
}

export function publishChecks({
  findings,
  goesLive,
  otherChecks = null,
  template = null,
  draftPaths,
}: PublishCheckInput): PublishChecks {
  const rows: PublishRow[] = [];
  const templatePage = (slug: string) => template?.pages.find((p) => p.slug === slug) || null;

  // The front page: the site opens on a page that won't be live.
  const home = findings.find((f) => f.code === 'home_page_not_live');
  if (home) {
    const slug = String((home as Finding & { homePage?: string }).homePage || '');
    const page = templatePage(slug);
    const name = page?.title || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Home');
    rows.push({
      key: 'home',
      kind: 'fix',
      text: `${page && template ? template.name : 'Your site'} opens on ${/^[aeiou]/i.test(name) ? 'an' : 'a'} ${name} page, and yours isn’t live.`,
      sub: 'Publish one with the theme, or keep your latest articles as the front page.',
      actions: [
        ...(page ? [{ id: 'addHome' as const, label: `Add ${page.title} to this publish` }] : []),
        { id: 'keepArticles', label: 'Keep latest articles' },
      ],
    });
  }

  // Menu links: one row for all of them, and the way to the menu.
  const issues = navIssues(findings, { draftPaths });
  const menuPart: NavPart = issues.some((i) => i.part === 'header') ? 'header' : 'footer';
  if (issues.length) {
    const byHref = new Map<string, (typeof issues)[number]>();
    for (const i of issues)
      if (!byHref.has(`${i.kind}:${i.href}`)) byHref.set(`${i.kind}:${i.href}`, i);
    const all = [...byHref.values()];
    const empty = all.filter((i) => i.kind === 'empty');
    const notLive = all.filter((i) => i.kind === 'missing' && i.notLive);
    const missing = all.filter((i) => i.kind === 'missing' && !i.notLive);
    const n = all.length;
    // "5 go to sections …, 4 to pages you don't have (About, Tips…)"; one
    // kind alone: "They go to sections …".
    const groups = [
      [empty, 'a section with no published articles', 'sections with no published articles'],
      [notLive, 'a page that isn’t live yet', 'pages that aren’t live yet'],
      [missing, 'a page you don’t have', 'pages you don’t have'],
    ] as const;
    const named = (list: typeof all, words: string) =>
      list === empty ? words : `${words} (${someNames(list.map((i) => i.label))})`;
    const present = groups.filter(([list]) => list.length);
    const sub =
      present.length > 1
        ? present
            .map(
              ([list, one, many], k) =>
                `${list.length} ${k === 0 ? (list.length === 1 ? 'goes ' : 'go ') : ''}to ${named(list, list.length === 1 ? one : many)}`
            )
            .join(', ')
        : `${n === 1 ? 'It goes' : 'They go'} to ${named(present[0][0], n === 1 ? present[0][1] : present[0][2])}`;
    // Menu links only warn (owner 2026-10-28); a server from before that
    // still refuses a link to nothing, so it says Fix first there.
    const blocking = findings.some((f) => isMenuLinkFinding(f) && f.severity === 'blocking');
    const addable = missing.length > 0 && missing.every((i) => templatePage(slugOf(i.href)));
    rows.push({
      key: 'menu',
      kind: blocking ? 'fix' : 'check',
      text: `${n} menu ${n === 1 ? 'link leads' : 'links lead'} nowhere yet.`,
      sub: `${sub}. Readers see an empty page.`,
      actions: [
        ...(addable ? [{ id: 'addPages' as const, label: 'Add the pages' }] : []),
        { id: 'editMenu', label: 'Edit the menu →' },
      ],
    });
  }

  // Sections readers can open today, with published articles, that a
  // template's Look and pages drops (backlog 63c15b98).
  const removed = findings.find((f) => f.code === 'section_removed');
  const sections: RemovedSection[] =
    (removed as (Finding & { sections?: RemovedSection[] }) | undefined)?.sections || [];
  if (removed && sections.length) {
    const one = sections.length === 1;
    rows.push({
      key: 'sections',
      kind: removed.severity === 'blocking' ? 'fix' : 'check',
      text: `${one ? 'A section' : `${sections.length} sections`} with published articles ${one ? 'leaves' : 'leave'} the menu: ${andList(sections.map((x) => x.name))}.`,
      sub: `${one ? 'Its page stops' : 'Their pages stop'} working (${sections.map((x) => `/${x.slug}`).join(', ')}); the articles stay.`,
      actions: [
        { id: 'keepSections', label: one ? 'Keep it' : 'Keep them' },
        { id: 'editMenu', label: 'Edit the menu →' },
      ],
    });
  }

  // Everything else about the site, in its own words. Custom CSS has its own
  // section (it never holds Publish); suggestions aren't problems.
  for (const [i, f] of findings.entries()) {
    if (
      f.code === 'home_page_not_live' ||
      f.code === 'css_hooks' ||
      f.code === 'section_removed' ||
      isMenuLinkFinding(f)
    )
      continue;
    if (f.advisory) continue;
    rows.push({
      key: `f${i}`,
      kind: f.severity === 'blocking' ? 'fix' : 'check',
      text: `${(f.label || f.message || f.code).replace(/\.?$/, '.')}`,
      sub: '',
      actions: [],
    });
  }

  // A page or form that goes with it has something to fix.
  if (otherChecks?.blocking) {
    const first = (otherChecks.first || 'Something on a page').replace(/\.?$/, '.');
    rows.push({
      key: 'pages',
      kind: 'fix',
      text: first,
      sub: otherChecks.more
        ? `And ${otherChecks.more} more on the pages that publish with it.`
        : 'On a page that publishes with it.',
      actions: [{ id: 'reviewPages', label: 'Review pages →' }],
    });
  }

  if (goesLive.length) {
    const names = [
      ...goesLive.filter((g) => g.kind === 'page').map((g) => g.title || 'Untitled page'),
      ...goesLive.filter((g) => g.kind === 'form').map((g) => `the ${g.title || 'untitled'} form`),
    ];
    const one = goesLive.length === 1;
    rows.push({
      key: 'live',
      kind: 'live',
      text: `${andList(names)} ${one ? 'publishes' : 'publish'} with this theme.`,
      sub: one ? 'It’s a draft until then.' : 'They’re drafts until then.',
      actions: [{ id: 'reviewPages', label: 'Review pages →' }],
    });
  }

  // Fix first, then checks, then what goes live.
  const order = { fix: 0, check: 1, live: 2 };
  rows.sort((a, b) => order[a.kind] - order[b.kind]);
  const nFix = rows.filter((r) => r.kind === 'fix').length;
  const nCheck = rows.filter((r) => r.kind === 'check').length;
  const counts = [nFix ? `${nFix} to fix` : '', nCheck ? `${nCheck} to check` : ''].filter(Boolean);
  return {
    rows,
    head: counts.length ? `Before you publish · ${counts.join(', ')}` : 'What publishes',
    blockedTip: !nFix ? null : home ? 'Fix the front page first' : 'Fix what’s marked Fix first',
    menuPart,
  };
}
